import { WebPageEvents } from '@griply/types';
import { CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sizes } from 'shared/components/ui/button';
import { useSubscriptionPrices } from 'shared/hooks/use-subscription-prices';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { currencySignMap, Price, PriceInterval } from 'shared/types/price';

import { PlanToggle } from '../components/plan-toggle';
import { PremiumLayout } from '../components/premium-layout';
import { PriceDisplay } from '../components/price-display';
import * as Styled from './premium.style';

export type PremiumProps = {
  onSkip: () => void;
  isLoading: boolean;
  onSubmit: (price?: Price) => void;
  initialPriceSelection?: PriceInterval;
};

export const Premium: React.FC<PremiumProps> = ({
  onSkip,
  isLoading,
  onSubmit,
  initialPriceSelection = PriceInterval.Year,
}) => {
  useTrackPageVisitEvent(WebPageEvents.OnboardingPremium, undefined);
  const { t } = useTranslation();

  const { prices } = useSubscriptionPrices();
  const annualPrice = prices.find(
    ({ interval }) => interval === PriceInterval.Year,
  );
  const monthlyPrice = prices.find(
    ({ interval }) => interval === PriceInterval.Month,
  );

  const [isAnnual, setIsAnnual] = useState(
    initialPriceSelection !== PriceInterval.Month,
  );

  const selectedPrice = isAnnual ? annualPrice : monthlyPrice;
  const currencySign = selectedPrice
    ? currencySignMap[selectedPrice.currency] ?? selectedPrice.currency
    : '';
  // intentionally ignores discount/coupon — the onboarding paywall shows full price only
  const perMonthCents = selectedPrice
    ? isAnnual
      ? Math.round(selectedPrice.amountInCents / 12)
      : selectedPrice.amountInCents
    : 0;
  const bigPrice = `${currencySign}${(perMonthCents / 100).toFixed(2)}`;
  const caption = isAnnual
    ? t('forms.onboarding.premium.price.per-month-annually', {
        price: `${currencySign}${((annualPrice?.amountInCents ?? 0) / 100).toFixed(2)}`,
      })
    : t('forms.onboarding.premium.price.per-month-monthly');

  const submitSelection = () => onSubmit(selectedPrice);

  return (
    <PremiumLayout
      onSkip={onSkip}
      toggle={
        <PlanToggle
          isAnnual={isAnnual}
          onChange={setIsAnnual}
          disabled={isLoading}
        />
      }
      price={<PriceDisplay amount={bigPrice} caption={caption} />}
      cta={
        <Styled.CtaButton
          onClick={submitSelection}
          type="button"
          size={Sizes.Large}
          disabled={isLoading || !selectedPrice}
          fullWidth
          alignCenter
          disabledBackgroundOnly
        >
          {isLoading ? (
            <CircularProgress color="primary" size={20} />
          ) : (
            t('dialogs.premium.interactions.confirm.trial')
          )}
        </Styled.CtaButton>
      }
    />
  );
};
